// Top-level layout + router. Every route is wrapped in <Topbar /> +
// <ComposeModal /> so they render everywhere consistently.

import { Routes, Route } from 'react-router-dom';
import { Topbar } from './components/Topbar';
import { ComposeModal } from './components/ComposeModal';
import { WelcomeModal } from './components/WelcomeModal';
import { InstallPrompt } from './components/InstallPrompt';
import { PresenceHeartbeat } from './components/PresenceHeartbeat';
import { Paper } from './pages/Paper';
import { PostView } from './pages/PostView';
import { Diary } from './pages/Diary';
import { Places } from './pages/Places';
import { Classifieds } from './pages/Classifieds';
import { Messages } from './pages/Messages';
import { Profile } from './pages/Profile';
import { Manage } from './pages/Manage';
import { GroupDashboard } from './pages/GroupDashboard';
import { GroupProfile } from './pages/GroupProfile';
import { CommunityDashboard } from './pages/CommunityDashboard';
import { CommunityNewsroom } from './pages/CommunityNewsroom';
import { CommunityLayout }   from './pages/CommunityLayout';
import { CommunitySettings } from './pages/CommunitySettings';
import { CommunityBoundaryEditor } from './pages/CommunityBoundaryEditor';
import { Community } from './pages/Community';
import { UserProfile } from './pages/UserProfile';
import { Settings } from './pages/Settings';
import { PartnerDashboard } from './pages/PartnerDashboard';
import { BusinessOnboarding } from './pages/BusinessOnboarding';
import { Admin } from './pages/Admin';
import { AIAgents } from './pages/AIAgents';
import { AdminNewsroom } from './pages/AdminNewsroom';
import { AdminMap } from './pages/AdminMap';
import { AdminClaims } from './pages/AdminClaims';
import { AdminAnalytics } from './pages/AdminAnalytics';
import { AdminFeeds } from './pages/AdminFeeds';
import { PolicyPage } from './pages/PolicyPage';
import privacyMd    from './content/policies/privacy.md?raw';
import rulesMd      from './content/policies/rules.md?raw';
import editorCodeMd from './content/policies/editor-code.md?raw';

// Effective date shown on the policy pages. Bump this whenever any of the
// three markdown docs above is materially changed — the WelcomeModal
// says "we'll tell you" when policy changes.
const POLICY_EFFECTIVE = '16 August 2026';

export default function App() {
    return (
        <>
            <Topbar />
            <Routes>
                <Route path="/"            element={<Paper />} />
                <Route path="/posts/:id"   element={<PostView />} />
                <Route path="/diary"       element={<Diary />} />
                <Route path="/places"      element={<Places />} />
                <Route path="/classifieds" element={<Classifieds />} />
                <Route path="/messages"    element={<Messages />} />
                <Route path="/profile"     element={<Profile />} />
                <Route path="/manage"      element={<Manage />} />
                <Route path="/groups/:slug" element={<GroupProfile />} />
                <Route path="/groups/:slug/dashboard" element={<GroupDashboard />} />
                <Route path="/communities/:slug/dashboard" element={<CommunityDashboard />} />
                <Route path="/communities/:slug/newsroom"  element={<CommunityNewsroom />} />
                <Route path="/communities/:slug/layout"    element={<CommunityLayout />} />
                <Route path="/communities/:slug/settings"  element={<CommunitySettings />} />
                <Route path="/communities/:slug/boundary"  element={<CommunityBoundaryEditor />} />
                {/* Plural is the canonical route — matches /communities/:slug/dashboard
                    and /communities/:slug/boundary, and matches the URL the search
                    backend emits. Singular kept as a compat alias so any bookmarks
                    or shared links from before the standardization keep working. */}
                <Route path="/community/:slug"  element={<Community />} />
                <Route path="/communities/:slug" element={<Community />} />
                <Route path="/user/:id" element={<UserProfile />} />
                <Route path="/settings"    element={<Settings />} />
                <Route path="/partners"    element={<PartnerDashboard />} />
                <Route path="/onboarding"  element={<BusinessOnboarding />} />
                <Route path="/admin"           element={<Admin />} />
                <Route path="/admin/ai-agents" element={<AIAgents />} />
                <Route path="/admin/newsroom"  element={<AdminNewsroom />} />
                <Route path="/admin/map"       element={<AdminMap />} />
                <Route path="/admin/claims"    element={<AdminClaims />} />
                <Route path="/admin/analytics" element={<AdminAnalytics />} />
                <Route path="/admin/feeds"     element={<AdminFeeds />} />
                <Route path="/privacy" element={
                    <PolicyPage
                        kicker="OnHere · Legal"
                        title="Privacy Policy"
                        subtitle="What we collect, what we don't, and how POPIA applies to you."
                        effective={POLICY_EFFECTIVE}
                        bodyMd={privacyMd}
                    />
                } />
                <Route path="/rules" element={
                    <PolicyPage
                        kicker="OnHere · Community"
                        title="Community Rules"
                        subtitle="What gets posts published, returned, or removed. Written for humans."
                        effective={POLICY_EFFECTIVE}
                        bodyMd={rulesMd}
                    />
                } />
                <Route path="/editor-code" element={
                    <PolicyPage
                        kicker="OnHere · Editors"
                        title="Editor Code of Conduct"
                        subtitle="The standard we hold ourselves — and any AI editor — to."
                        effective={POLICY_EFFECTIVE}
                        bodyMd={editorCodeMd}
                    />
                } />
                <Route path="*"            element={<NotFound />} />
            </Routes>
            <ComposeModal />
            <WelcomeModal />
            <InstallPrompt />
            <PresenceHeartbeat />
        </>
    );
}

function NotFound() {
    return (
        <div style={{ padding: '80px 24px', textAlign: 'center', color: '#666' }}>
            <h1 style={{ fontFamily: 'Newsreader, serif' }}>Not found</h1>
            <p>The page you're looking for isn't here.</p>
            <p><a href="/">Back to the paper →</a></p>
        </div>
    );
}
